import { Select, ActionIcon } from '@mantine/core'
import { useState, useEffect, useRef } from 'react'
import { IconSearch, IconUserPlus, IconX } from '@tabler/icons-react'
import { useDebounce } from '@/hooks'
import { useMedplum } from '@medplum/react'
import { Patient } from '@medplum/fhirtypes'
import { formatPatientName } from '@/utils.ts'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import AddNewPatientModal from '@/components/ui/CreateUpdatePatientModal/CreateUpdatePatientModal'
import { buildPatientLookupSearchQuery } from '@/utils/patientSearch'

export const PatientSelect = ({
  setSelectedPatient,
  placeholder = null,
  hideAddPatientButton = false,
  initialPatient = null,
  fullWidth = false,
}: {
  setSelectedPatient: (patientId: Patient | null) => void
  placeholder?: string | null
  hideAddPatientButton?: boolean
  /**
   * Fill the container instead of the fixed 300px. For a caller narrower than
   * that — the Messages filter popover — where the fixed width overflows the
   * card and leaves the field misaligned with everything under it.
   */
  fullWidth?: boolean
  /**
   * Patient to show as already picked on mount, for a caller that remembers the
   * search across a remount (PD-1206). Omitted everywhere else, which leaves
   * the control empty exactly as before.
   */
  initialPatient?: Patient | null
}) => {
  const [searchQueryText, setSearchQueryText] = useState('')
  const [selectPatientOptions, setSelectPatientOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [patientsMap, setPatientsMap] = useState<{ [key: string]: Patient }>({})
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    initialPatient?.id ?? null,
  )
  const [patients, setPatients] = useState<Patient[] | []>(initialPatient ? [initialPatient] : [])
  const [searchQueryStr, setSearchQueryStr] = useState('')
  const [searchIsLoading, setSearchIsLoading] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQueryText, 500)
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false)
  const handlePatientChange = (patientOption: string | null) => {
    if (patientOption) {
      const patient = patientsMap[patientOption]
      setSelectedPatientId(patientOption)
      if (patient) {
        setSelectedPatient(patient)
      }
    }
  }
  const medplum = useMedplum()
  // This effect runs once on mount with an empty query, which would wipe an
  // `initialPatient` before the user has touched anything (PD-1206). Skip that
  // first clear only; every later one still applies.
  const skipInitialClear = useRef(Boolean(initialPatient))
  useEffect(() => {
    if (debouncedSearchQuery.length > 2) {
      setSearchQueryStr(buildPatientLookupSearchQuery(debouncedSearchQuery))
    } else {
      if (skipInitialClear.current) {
        skipInitialClear.current = false
        return
      }
      setSelectedPatient(null)
      setSelectPatientOptions([])
      setPatients([])
      setPatientsMap({})
      setSearchQueryStr('')
      setSearchIsLoading(false)
    }
  }, [debouncedSearchQuery])
  useEffect(() => {
    let cancelled = false

    if (medplum && searchQueryStr && debouncedSearchQuery.length > 2) {
      setSearchIsLoading(true)
      medplum
        .search('Patient', searchQueryStr)
        .then((result) => {
          if (cancelled) return
          const patientList = result.entry?.map((entry) => entry.resource as Patient) || []
          setPatients(patientList)
          setPatientsMap(Object.fromEntries(patientList.map((i) => [i.id, i])))
        })
        .catch((e) => {
          if (cancelled) return
          logError('call-center', 'Failed to fetch patients', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch patients',
            color: 'red',
          })
        })
        .finally(() => {
          if (!cancelled) {
            setSearchIsLoading(false)
          }
        })
    }

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, searchQueryStr])
  useEffect(() => {
    if (patients) {
      const filteredOptions = patients.map((patient) => {
        return {
          value: patient.id!,
          label: patient.name?.[0] ? formatPatientName(patient.name[0]) : '',
        }
      })
      setSelectPatientOptions(filteredOptions)
    } else {
      setSelectPatientOptions([])
    }
  }, [patients])

  return (
    <>
      <AddNewPatientModal
        isOpen={isAddPatientModalOpen}
        onClose={() => setIsAddPatientModalOpen(false)}
        successCallback={(patient) => {
          setSelectPatientOptions((prevOptions) => [
            ...prevOptions,
            {
              value: patient.id!,
              label: patient.name?.[0] ? formatPatientName(patient.name[0]) : '',
            },
          ])
          setSelectedPatient(patient)
          setSelectedPatientId(patient.id || null)
          setIsAddPatientModalOpen(false)
        }}
      />

      <Select
        onSearchChange={(text) => {
          setSearchQueryText(text)
          if (!text && selectedPatientId) {
            setSelectedPatientId(null)
            setSelectedPatient(null)
          }
        }}
        onFocus={() => setSelectPatientOptions([])}
        searchable
        data={selectPatientOptions}
        value={selectedPatientId}
        name={`patient-name`}
        onChange={handlePatientChange}
        width={400}
        classNames={{
          input: 'h-9 rounded-md border border-gray-300 text-sm',
          root: fullWidth ? 'w-full' : 'w-[300px]',
        }}
        leftSection={<IconSearch size={14} className="opacity-50" />}
        rightSection={
          selectedPatientId || searchQueryText || !hideAddPatientButton ? (
            <div className="flex items-center gap-1">
              {(selectedPatientId || searchQueryText) && (
                <ActionIcon
                  variant="transparent"
                  aria-label="Clear selection"
                  onClick={() => {
                    setSelectedPatientId(null)
                    setSelectedPatient(null)
                    setSearchQueryText('')
                    setSelectPatientOptions([])
                  }}
                >
                  <IconX size={16} className="opacity-60" />
                </ActionIcon>
              )}
              {!hideAddPatientButton && (
                <ActionIcon variant="transparent" onClick={() => setIsAddPatientModalOpen(true)}>
                  <IconUserPlus size={18} />
                </ActionIcon>
              )}
            </div>
          ) : null
        }
        placeholder={placeholder || 'Find appointments by patient...'}
        comboboxProps={{ withinPortal: true }}
        allowDeselect={false}
        rightSectionPointerEvents="all"
        rightSectionWidth={
          (selectedPatientId || searchQueryText) && !hideAddPatientButton ? 64 : 36
        }
        nothingFoundMessage={
          searchQueryText.length > 1 &&
          patients.length === 0 &&
          !searchIsLoading && (
            <div className="space-between justify-space-between flex w-full items-center gap-2 p-0">
              <p>No patients found</p>
            </div>
          )
        }
      />
    </>
  )
}
